// pages/SAPConfiguration.jsx — SAP Configuration (SPRO).
// Dipisah dari SAP Customizing & Enhancement (27-10-2026): konfigurasi (data
// tabel customizing via SPRO/IMG, customizing request) dan enhancement (objek
// repository/workbench) adalah dua hal berbeda. Isi halaman = lapis config yang
// sebelumnya tab "Config layer (SPRO)": SAPConfigLayer (+ SAPSproPanel) dan
// jadwal sync tabel config. Permission tetap 'sap_customizing' — backend tidak
// berubah. Teks UI bahasa Inggris (literal).
// 29-10-2026: tambah tampilan kedua "Process Map" (SAPProcessMap) — swimlane
// proses bisnis P01 ID/JB/KL, deep link ?view=process (atau ?tab=process).
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useActiveDashboard } from '../context/ActiveDashboardContext';
import { CronScheduler } from './tmShared';
import SAPConfigLayer from './SAPConfigLayer';
// 04-11-2026: Process Map dipindah ke grup DOCS (/dashboard/doc-process-map, perm doc_process_map).
// Tombol "Process Map ↗" di sini hanya shortcut; ?view=process diarahkan ke halaman baru.

function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

export default function SAPConfiguration() {
  const { t } = useLang();
  const api = useApi();
  const [conns, setConns]   = useState([]);
  const [connId, setConnId] = useState('');
  const [err, setErr]       = useState('');
  const [sproFocus, setSproFocus] = useState(0);
  const [view, setView]     = useState('config'); // 'config' | 'process'
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } })();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activePageKey  = useActiveDashboard();
  useEffect(() => {
    if (activePageKey !== 'sap_config') return;
    const conn = searchParams.get('conn');
    if (conn && /^\d+$/.test(conn)) setConnId(String(conn));
    if (searchParams.get('view') === 'recent' || searchParams.get('tab') === 'recent') { setView('config'); setSproFocus(Date.now()); }
    // Process Map pindah ke grup DOCS (04-11-2026) — link lama diarahkan ke halaman baru.
    if (searchParams.get('view') === 'process' || searchParams.get('tab') === 'process') {
      const qp = new URLSearchParams(searchParams); qp.delete('view'); qp.delete('tab');
      const qs = qp.toString();
      navigate('/dashboard/doc-process-map' + (qs ? '?' + qs : ''), { replace: true });
    }
  }, [activePageKey, searchParams]); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    api('/api/sap/connections')
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d) ? d : (d.rows || d.connections || []);
        setConns(list);
        const first = list.find(c => c.pathReadtable) || list[0];
        if (first) setConnId(prev => prev || String(first.id));
      })
      .catch(e => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api]);

  return (
    <div style={{ padding: '1.25rem', maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 14,
        background: 'linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)',
        borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ flex: '1 1 260px' }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>
            SAP Configuration (SPRO)
          </h1>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
            {view === 'process'
              ? 'Business process map of P01 ID, JB and KL — document types, volumes and custom points per step'
              : 'Customizing data set via SPRO/IMG — config TRs, SPRO navigator, change log and system comparison'}
          </div>
        </div>
        <div role="tablist" aria-label="View"
          style={{ display: 'inline-flex', padding: 3, borderRadius: 10, background: 'rgba(255,255,255,0.18)', gap: 2 }}>
          {[['config', 'Configuration'], ['process', 'Process Map ↗']].map(([k, label]) => (
            <button key={k} role="tab" aria-selected={view === k}
              title={k === 'process' ? 'Process Map has moved to Documentation → SAP Process Map' : undefined}
              onClick={() => k === 'process' ? navigate('/dashboard/doc-process-map') : setView(k)}
              style={{ padding: '0.45rem 0.9rem', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                background: view === k ? '#fff' : 'transparent', color: view === k ? 'rgb(var(--brand-700))' : '#fff' }}>
              {label}
            </button>
          ))}
        </div>
        {view === 'config' && (
          <>
            <select value={connId} onChange={e => setConnId(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', minWidth: 200 }}>
              {conns.map(c => (
                <option key={c.id} value={c.id}>
                  {[c.landscape, c.environment, c.sid].filter(Boolean).join(' / ')} — {c.host} ({c.client})
                </option>
              ))}
            </select>
            <button onClick={() => setSproFocus(Date.now())}
              title="Open SPRO navigator → Recently changed (table change log)"
              style={{ padding: '0.55rem 1rem', borderRadius: 8, fontSize: 13, fontWeight: 700,
                border: '1px solid #fde68a', background: '#fffbeb', color: '#92400e', cursor: 'pointer' }}>
              Recent SPRO changes
            </button>
          </>
        )}
      </div>


      {view === 'config' && (
        <>
          <div style={{ fontSize: 12.5, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: 14, lineHeight: 1.5 }}>
            <strong>Configuration vs enhancement:</strong> this page covers <em>configuration</em> — values in customizing tables
            (client-dependent, customizing requests). User exits, BAdIs and other ABAP <em>enhancements</em> are repository
            objects and live in <strong>ABAP Workbench → Enhancements</strong>.
          </div>

          {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}

          {isAdmin && (
            <CronScheduler api={api} jobKey="sap_cfg_sync"
              title="⏰ Customizing Table Sync (D01/130 + T001B P01/310)"
              runConfirm="Read the configured customizing tables (incl. FI validation GB9x and Z/Y tcodes) from D01/130 and T001B from P01/310 via RFC_READ_TABLE (read-only). This takes a few minutes. Continue?" />
          )}
          {connId && <SAPConfigLayer api={api} t={t} connId={connId} sproFocus={sproFocus} />}
        </>
      )}
    </div>
  );
}
